import { useEffect, useRef, useState } from "react";
import { EditorState } from "draft-js";
import { arrayMove } from "@dnd-kit/sortable";
import { richTextContentFromHtml } from '../../shared/utils/richTextHtml';
import { toast } from "react-toastify";
import axios from "axios";

export const toastError = (error) => {
  const data = error && error.response && error.response.data;
  toast.error(Array.isArray(data) ? data.join(", ") : "Something went wrong", {
    position: toast.POSITION.TOP_CENTER,
    draggable: false,
    closeOnClick: false,
    autoClose: 5000,
    hideProgressBar: true,
  });
};

const htmlToEditorState = (html) =>
  EditorState.createWithContent(richTextContentFromHtml(html || ""));

const noteFromServerJSON = (note) =>
  Object.assign({}, note, {
    body: note.body || "",
    editorState: htmlToEditorState(note.body || ""),
  });

export default function useNotes(initialData, { team, csrfToken, confirm, onNotesChanged }) {
  const seed = initialData || {};
  const [notes, setNotes] = useState(seed.notes || []);
  const [noteTemplates, setNoteTemplates] = useState(seed.noteTemplates || []);
  const [notesPageSection, setNotesPageSection] = useState(seed.notesPageSection || {});
  const [openNote, setOpenNote] = useState({});
  const [openNoteOriginal, setOpenNoteOriginal] = useState({});
  const [openNoteLoading, setOpenNoteLoading] = useState(false);

  // Sync notes outward (e.g. ConfirmShow keeps its Redux store in step for
  // ShowAdvanceSend) only on real changes — skip the initial seed.
  const mounted = useRef(false);
  const movePending = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (onNotesChanged) onNotesChanged(notes);
  }, [notes, onNotesChanged]);

  const setCsrf = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
  };

  const replaceNote = (note) =>
    setNotes((prev) => prev.map((n) => (n.id === note.id ? note : n)));

  const openNoteChanged = (note) => setOpenNote(note);
  const openNoteOriginalChanged = (note) => setOpenNoteOriginal(note);
  const openNoteTitleChanged = (title) =>
    setOpenNote((n) => Object.assign({}, n, { title }));
  const openNoteBodyChanged = (editorState) =>
    setOpenNote((n) => Object.assign({}, n, { editorState }));
  const disableOpenNotePreviewMode = () =>
    setOpenNote((n) => Object.assign({}, n, { previewMode: false }));
  const noteTemplatesChanged = (templates) => setNoteTemplates(templates);
  const noteUpdated = (note) => replaceNote(note);
  const updateConfirmPageSection = (section) => setNotesPageSection(section);

  const fetchNewNote = () =>
    axios.get(`/teams/${team.id}/notes/new`).then(({ data }) => {
      openNoteChanged(data);
      openNoteOriginalChanged(data);
    });

  const closeOpenNote = (csrf, t, noteId) => {
    if (!noteId) {
      setOpenNote({});
      return Promise.resolve();
    }
    setCsrf();
    return axios
      .get(`/teams/${team.id}/notes/${noteId}`)
      .then(({ data }) => {
        replaceNote(noteFromServerJSON(data));
        setOpenNote({});
      })
      .catch(() => setOpenNote({}));
  };

  const createNote = (csrf, t, conf, note) => {
    setCsrf();
    setOpenNoteLoading(true);
    return axios
      .post(`/teams/${team.id}/notes`, {
        note: {
          calendar_event_id: (conf || confirm).id,
          title: note.title,
          body: note.body,
          note_template_id: note.note_template_id,
        },
      })
      .then(({ data }) => {
        setOpenNoteLoading(false);
        const saved = noteFromServerJSON(data);
        setNotes((prev) => [...prev, saved]);
        openNoteChanged(saved);
        openNoteOriginalChanged(saved);
      })
      .catch((error) => {
        setOpenNoteLoading(false);
        toastError(error);
      });
  };

  const deleteNote = (csrf, t, note) => {
    setCsrf();
    return axios
      .delete(`/teams/${team.id}/notes/${note.id}`)
      .then(({ data }) => setNotes((prev) => prev.filter((n) => n.id !== data.id)))
      .catch(() => {});
  };

  const fetchNoteToEdit = (csrf, t, noteId) =>
    axios.get(`/teams/${team.id}/notes/${noteId}/edit`).then(({ data }) => {
      const note = Object.assign({}, noteFromServerJSON(data), { previewMode: false });
      openNoteChanged(note);
      openNoteOriginalChanged(note);
    });

  const fetchNoteToPreview = (csrf, t, noteId) =>
    axios.get(`/teams/${team.id}/notes/${noteId}/edit`).then(({ data }) => {
      const note = Object.assign({}, noteFromServerJSON(data), { previewMode: true });
      openNoteChanged(note);
      openNoteOriginalChanged(note);
    });

  const moveNote = (noteId, targetNoteId) => {
    if (movePending.current) return Promise.resolve();
    movePending.current = true;
    const from = notes.findIndex((n) => n.id === noteId);
    const to = notes.findIndex((n) => n.id === targetNoteId);
    setNotes(arrayMove(notes, from, to));
    setCsrf();
    return axios
      .patch(`/teams/${team.id}/notes/${noteId}/move`, { target_note_id: targetNoteId })
      .catch((error) => {
        setNotes((current) => {
          const at = current.findIndex((n) => n.id === noteId);
          if (at === -1) return current;
          return arrayMove(current, at, Math.min(from, current.length - 1));
        });
        toastError(error);
      })
      .finally(() => {
        movePending.current = false;
      });
  };

  const updateNote = (csrf, t, note) => {
    setCsrf();
    setOpenNoteLoading(true);
    return axios
      .patch(`/teams/${team.id}/notes/${note.id}`, {
        note: {
          title: note.title,
          body: note.body,
          note_template_id: note.note_template_id,
        },
      })
      .then(({ data }) => {
        setOpenNoteLoading(false);
        const saved = noteFromServerJSON(data);
        replaceNote(saved);
        openNoteChanged(saved);
        openNoteOriginalChanged(saved);
      })
      .catch((error) => {
        setOpenNoteLoading(false);
        toastError(error);
      });
  };

  return {
    notes,
    noteTemplates,
    notesPageSection,
    openNote,
    openNoteOriginal,
    openNoteLoading,
    openNoteChanged,
    openNoteOriginalChanged,
    openNoteTitleChanged,
    openNoteBodyChanged,
    disableOpenNotePreviewMode,
    noteTemplatesChanged,
    noteUpdated,
    updateConfirmPageSection,
    fetchNewNote,
    closeOpenNote,
    createNote,
    deleteNote,
    fetchNoteToEdit,
    fetchNoteToPreview,
    updateNote,
    moveNote,
  };
}
